import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircleOutline, MailOutline } from '@mui/icons-material';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getPortalBranding, requestMagicLink } from '@/api/portal';
import { toApiError } from '@/api/client';
import { PortalBrandHeader } from '@/components/PortalBrandHeader';
import { safeMailtoHref } from '@/lib/safeExternalUrl';

export interface LoginSearch {
  t?: string; // tenantId (lo trae el link público del estudio)
}

export const Route = createFileRoute('/portal/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    t: typeof search.t === 'string' ? search.t : undefined,
  }),
  component: PortalLogin,
});

function PortalLogin(): JSX.Element {
  const { t: tenantFromUrl } = Route.useSearch();
  const [tenantId, setTenantId] = useState(tenantFromUrl ?? '');
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  // Branding del estudio para la pantalla (anónimo, por tenantId). Sólo si viene en la URL.
  const brandingQuery = useQuery({
    queryKey: ['portal-branding', tenantFromUrl],
    queryFn: () => getPortalBranding(tenantFromUrl as string),
    enabled: Boolean(tenantFromUrl),
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: () => requestMagicLink(tenantId.trim(), email.trim()),
    onSuccess: () => setSent(true),
  });

  const tenantMissing = tenantId.trim() === '';
  const emailMissing = email.trim() === '';

  return (
    <Box>
      <PortalBrandHeader
        branding={brandingQuery.data ?? null}
        subtitle="Portal de clientes"
      />

      <Paper variant="outlined" sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
        {sent ? (
          <Stack spacing={2} alignItems="center" textAlign="center" sx={{ py: 2 }}>
            <CheckCircleOutline color="success" sx={{ fontSize: 48 }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Revisá tu correo
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Si <strong>{email.trim()}</strong> corresponde a un cliente registrado, te enviamos un
              enlace de acceso. El enlace vence en pocos minutos.
            </Typography>
            <Button
              variant="text"
              onClick={() => {
                setSent(false);
                mutation.reset();
              }}
            >
              Usar otro correo
            </Button>
          </Stack>
        ) : (
          <Stack
            component="form"
            spacing={2.5}
            onSubmit={(e) => {
              e.preventDefault();
              if (!tenantMissing && !emailMissing) {
                mutation.mutate();
              }
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Ingresá a tu cuenta
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Te enviaremos un enlace de acceso por correo. No necesitás contraseña.
              </Typography>
            </Box>

            {mutation.isError ? (
              <Alert severity="error">{toApiError(mutation.error).message}</Alert>
            ) : null}

            {!tenantFromUrl ? (
              <TextField
                label="ID del estudio"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                fullWidth
                size="small"
                helperText="Pedíselo a tu estudio si no tenés el enlace directo."
              />
            ) : null}

            <TextField
              label="Correo electrónico"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              size="small"
              autoComplete="email"
              autoFocus
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={tenantMissing || emailMissing || mutation.isPending}
              startIcon={
                mutation.isPending ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <MailOutline />
                )
              }
            >
              Enviarme el enlace
            </Button>
          </Stack>
        )}
      </Paper>

      {safeMailtoHref(brandingQuery.data?.supportEmail) ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
          ¿Problemas para ingresar?{' '}
          <MuiLink href={safeMailtoHref(brandingQuery.data?.supportEmail)!}>
            {brandingQuery.data?.supportEmail}
          </MuiLink>
        </Typography>
      ) : null}
    </Box>
  );
}
